import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getPsychologists } from '../hooks/api/psychologist/list';
import type { Psychologist } from '../types/psychologist';
import apiCoreAdapter from '../hooks/apiCoreAdapter';
import { SESSION_APPOINTMENT_TYPES } from '../hooks/api/config';
import { getSessionData } from '../hooks/api';
import { invalidatePatientsCache } from '../components/psychologist/calendar/data';

// Definir tipo local para AppointmentType
export type AppointmentType = {
  id: string;
  name: string;
  duration?: number;
  service?: string;
  external_id?: string;
};

export interface SelectedPsychologist {
  id: string;
  nickname?: string;
  name?: string;
  lastName?: string;
  specialties: string[];
  currency?: string;
  paymentMethod?: string;
  rankingCategory?: string;
  rankingScore?: number;
  processLimit?: number;
  processLimitPrefered?: number | null;
  statusId?: string;
  statusName?: string;
  timezone?: string | null;
}

export type PsychologistService = {
  id: string;
  service: {
    id: string;
    name: string;
    description?: string;
  };
};

interface PsychologistContextType {
  psychologist: SelectedPsychologist | null;
  setPsychologist: (p: SelectedPsychologist | null) => void;
  loadPsychologist: (id: string) => Promise<void>;
  clearPsychologist: () => void;
  services: PsychologistService[];
  servicesLoading: boolean;
  setServices: (services: PsychologistService[]) => void;
  setServicesLoading: (loading: boolean) => void;
  loadServices: () => Promise<void>;
  appointmentTypes: AppointmentType[];
}

const PsychologistContext = createContext<PsychologistContextType | undefined>(undefined);

export const PsychologistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [psychologist, setPsychologistState] = useState<SelectedPsychologist | null>(null);
  const [services, setServicesState] = useState<PsychologistService[]>([]);
  const [servicesLoading, setServicesLoadingState] = useState<boolean>(false);
  const [servicesLoaded, setServicesLoaded] = useState<boolean>(false);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);

  useEffect(() => {
    // Cargar información del psicólogo guardada, pero solo si hay una sesión válida (con fallback cross-tab)
    const stored = localStorage.getItem('psi-connect-psychologist');
    const session = getSessionData();
    if (stored && session?.user?.email) {
      try {
        const psychData = JSON.parse(stored);
        const sessionPsychId = session.psychologist_id ? String(session.psychologist_id) : null;
        if (psychData && sessionPsychId && String(psychData.id) !== sessionPsychId) {
          // Caché de otra psicóloga (mismo navegador, sesión distinta) → descartar
          localStorage.removeItem('psi-connect-psychologist');
        } else if (psychData) {
          setPsychologistState(psychData);
          // El cache puede estar viejo: timezone, ranking (N → F cambia qué pantallas ve),
          // límite de procesos y estado se refrescan desde la API en cada carga.
          if (psychData.id) {
            apiCoreAdapter.psychologistProfile()
              .then(profile => {
                if (!profile) return;
                const next = { ...psychData };
                if (profile.timezone !== undefined) next.timezone = profile.timezone;
                if (profile.ranking_category) next.rankingCategory = profile.ranking_category;
                if (profile.process_limit !== undefined) next.processLimit = profile.process_limit;
                if (profile.process_limit_preferred !== undefined) next.processLimitPrefered = profile.process_limit_preferred;
                if (profile.status_id) next.statusId = profile.status_id;
                if (JSON.stringify(next) !== JSON.stringify(psychData)) setPsychologist(next);
              })
              .catch(() => { /* noop */ });
          }
        } else {
          localStorage.removeItem('psi-connect-psychologist');
        }
      } catch {
        localStorage.removeItem('psi-connect-psychologist');
      }
    }
  }, []);

  const setPsychologist = (p: SelectedPsychologist | null) => {
    setPsychologistState(p);
    if (p) localStorage.setItem('psi-connect-psychologist', JSON.stringify(p));
    else localStorage.removeItem('psi-connect-psychologist');
  };

  const clearPsychologist = () => {
    // Limpia el cache en memoria de pacientes (dataset del popup "sin próxima sesión",
    // lista de pacientes y lead process ids). Se ejecuta en login y logout, así que un
    // dataset de una sesión anterior no sobrevive al cambio de identidad — el logout usa
    // navigate() (no recarga), por lo que estos Map de módulo persistirían si no se limpian.
    invalidatePatientsCache();
    setPsychologist(null);
    setServicesState([]);
    setServicesLoadingState(false);
    setServicesLoaded(false);
    setAppointmentTypes([]);
  };

  const loadPsychologist = async (id: string) => {
    const resp = await getPsychologists({ id, take: 1 });
    const list = resp.data as Psychologist[];
    const found = list.find(p => String(p.id) === String(id)) ?? null;
    if (!found) return;
    const specialties = (found.specialties as string[] | undefined) || (found.especialidades as string[] | undefined) || [];

    let rankingCategory = found.ranking_category;
    if (!rankingCategory) {
      try {
        const profile = await apiCoreAdapter.psychologistProfile();
        rankingCategory = profile.ranking_category as typeof found.ranking_category;
      } catch { /* noop */ }
    }

    const mapped: SelectedPsychologist = {
      id: String(found.id),
      nickname: found.nickname,
      name: found.name || (found as unknown as { nombre?: string }).nombre,
      lastName: found.last_name || (found as unknown as { apellido?: string }).apellido,
      specialties,
      currency: found.currency,
      paymentMethod: found.payment_method,
      rankingCategory,
      rankingScore: found.ranking_score,
      processLimit: found.process_limit,
      processLimitPrefered: found.process_limit_prefered ?? null,
      statusId: found.status?.id,
      statusName: found.status?.name,
      timezone: found.timezone ?? null,
    };
    setPsychologist(mapped);
  };

  const setServices = (services: PsychologistService[]) => {
    setServicesState(services);
  };

  const setServicesLoading = (loading: boolean) => {
    setServicesLoadingState(loading);
  };

  const loadServices = useCallback(async () => {
    if (servicesLoading || servicesLoaded) {
      return; // Ya se están cargando o ya están cargados
    }

    try {
      setServicesLoadingState(true);
      const result = await apiCoreAdapter.psychologistServices();
      setServicesState(Array.isArray(result) ? result : []);

      // Calcular appointment types basados en los servicios del psicólogo usando SESSION_APPOINTMENT_TYPES
      const serviceNames = (Array.isArray(result) ? result : []).map(s => s.service.name);
      const serviceMap = new Map((Array.isArray(result) ? result : []).map(s => [s.service.name, s.service.id]));
      const relevantAppointmentTypes = serviceNames
        .map(name => {
          const apt = SESSION_APPOINTMENT_TYPES.find(a => a.name === name);
          if (!apt) return null;
          return {
            id: apt.id,
            name: apt.name,
            duration: apt.duration || 60, // Use actual duration from SESSION_APPOINTMENT_TYPES
            service: serviceMap.get(name), // ID del servicio clínico
            external_id: apt.external_id
          };
        })
        .filter(Boolean) as AppointmentType[];
      setAppointmentTypes(relevantAppointmentTypes);
      setServicesLoaded(true);
    } catch (error) {
      console.error('Error loading services:', error);
      setServicesState([]);
      setAppointmentTypes([]);
      setServicesLoaded(true); // Mark as loaded even on error to prevent infinite loop
    } finally {
      setServicesLoadingState(false);
    }
  }, [servicesLoading, servicesLoaded]);

  return (
    <PsychologistContext.Provider value={{ 
      psychologist, 
      setPsychologist, 
      loadPsychologist, 
      clearPsychologist,
      services,
      servicesLoading,
      setServices,
      setServicesLoading,
      loadServices,
      appointmentTypes
    }}>
      {children}
    </PsychologistContext.Provider>
  );
};

export const usePsychologistContext = () => {
  const ctx = useContext(PsychologistContext);
  if (!ctx) throw new Error('usePsychologistContext must be used within PsychologistProvider');
  return ctx;
};


